import { useState } from "react";
import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const API_URL = process.env.REACT_APP_CHAT_API_URL || "";

export default function FindPatientResultsPage() {
  const [form, setForm] = useState({ firstName: "", lastName: "", dob: "", dateOfService: "", providerName: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/patient-results`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setSubmitted(true);
    } catch {
      setError("There was an issue submitting your request. Please call us at (877) 765-2274.");
    }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "12px 14px", border: `1.5px solid ${BRAND.lightGray}`, borderRadius: 8, fontFamily: "'Barlow', sans-serif", fontSize: 14, color: BRAND.textDark, outline: "none", background: BRAND.offWhite, boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.navy, marginBottom: 6 };

  return (
    <>
      <PageHero eyebrow="PATIENTS" title="Find Patient" highlight="Results" subtitle="Access your laboratory test results online or submit a request and our team will contact you with your results." />

      <div className="rx-pad" style={{ background: BRAND.offWhite, padding: "72px 32px 96px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          {/* Patient Results Portal card hidden — contact card expanded to full width */}
          {/* <a href="https://www.dxlink.com/patientportal/signin.html?cn=medlake&tn=medlake#/login" ...>Patient Results Portal</a> */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ background: BRAND.white, borderRadius: 16, padding: "40px 48px", border: `1px solid ${BRAND.lightGray}`, boxShadow: "0 4px 20px rgba(0,53,122,0.06)", display: "flex", alignItems: "center", gap: 48, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", color: BRAND.gold, marginBottom: 10 }}>CONTACT US</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.navy, margin: "0 0 12px" }}>Call Us Directly</h3>
                <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: BRAND.textMid, lineHeight: 1.7, marginBottom: 0 }}>Our customer service team can assist you with obtaining your results by phone during business hours.</p>
              </div>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, marginBottom: 4 }}>TOLL-FREE</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 32, color: BRAND.navy, marginBottom: 4 }}>(877) 765-2274</div>
                <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: BRAND.textMid, marginBottom: 10 }}>Monday – Friday · 8:00AM – 5:30PM</div>
                <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, marginBottom: 4 }}>DIRECT</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: BRAND.navy }}>(850) 460-1484</div>
              </div>
            </div>
          </div>

          {/* Manual request form */}
          <div style={{ background: BRAND.white, borderRadius: 16, border: `1px solid ${BRAND.lightGray}`, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,53,122,0.06)", maxWidth: 760, margin: "0 auto" }}>
            <div style={{ background: BRAND.navy, padding: "28px 36px", position: "relative" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: BRAND.gold }} />
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 26, color: BRAND.white, margin: "0 0 6px" }}>Request Results by Form</h2>
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 }}>A staff member will contact you regarding your results within one business day.</p>
            </div>
            <div style={{ padding: "36px" }}>
              {submitted ? (
                <div style={{ textAlign: "center", padding: "48px 20px" }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(0,53,122,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={BRAND.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.navy, marginBottom: 12 }}>Request Submitted!</h3>
                  <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.7 }}>Our team will contact you within one business day with your results.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                    {[["firstName", "First Name"], ["lastName", "Last Name"]].map(([name, label]) => (
                      <div key={name}>
                        <label style={labelStyle}>{label.toUpperCase()} *</label>
                        <input name={name} required value={form[name]} onChange={handleChange} style={inputStyle}
                          onFocus={e => e.target.style.borderColor = BRAND.navy}
                          onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                      </div>
                    ))}
                  </div>
                  <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={labelStyle}>DATE OF BIRTH *</label>
                      <input type="date" name="dob" required value={form.dob} onChange={handleChange} style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    <div>
                      <label style={labelStyle}>DATE OF SERVICE *</label>
                      <input type="date" name="dateOfService" required value={form.dateOfService} onChange={handleChange} style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>PROVIDER NAME *</label>
                    <input name="providerName" required value={form.providerName} onChange={handleChange} placeholder="Name of your ordering physician" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = BRAND.navy}
                      onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                  </div>
                  <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                    <div>
                      <label style={labelStyle}>PHONE NUMBER *</label>
                      <input type="tel" name="phone" required value={form.phone} onChange={handleChange} style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    <div>
                      <label style={labelStyle}>EMAIL ADDRESS</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                  </div>
                  <div style={{ background: "rgba(0,53,122,0.04)", borderRadius: 10, padding: "14px 18px", marginBottom: 20, border: `1px solid rgba(0,53,122,0.08)` }}>
                    <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, color: BRAND.textMid, margin: 0, lineHeight: 1.7 }}>
                      By submitting this form you authorize Labarium Diagnostic Systems to release your laboratory results to the contact information provided. For HIPAA purposes, results will be shared by phone or secure communication only.
                    </p>
                  </div>
                  {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", fontFamily: "'Barlow', sans-serif", fontSize: 13, color: "#b91c1c", marginBottom: 16 }}>{error}</div>}
                  <button type="submit" disabled={loading} style={{ width: "100%", background: loading ? BRAND.textMid : BRAND.navy, color: BRAND.white, border: "none", fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", padding: "16px 24px", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer" }}>
                    {loading ? "SUBMITTING…" : "REQUEST MY RESULTS →"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
